import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import {
  assertValidBlockXYWH,
  parseXYWH,
  serializeXYWH,
} from '@blocksuite/global/gfx';
import { effect, signal } from '@preact/signals-core';
import { createMutex } from 'lib0/mutex.js';
import * as Y from 'yjs';

import {
  Boxed,
  createYProxy,
  native2Y,
  type UnRecord,
  y2Native,
} from '../../reactive/index.js';
import type { Schema } from '../../schema/schema.js';
import type { Store } from '../store/store.js';
import { BlockModel } from './block-model.js';
import {
  shouldHydrateMissingDefaultProp,
  shouldPersistHydratedDefaultProp,
} from './default-props.js';
import type { YBlock } from './types.js';
import { isLocalYjsChange } from './yjs-origin.js';
import { internalPrimitives } from './zod.js';

declare const BUILD_CONFIG: { debug?: boolean } | undefined;

const CANVAS_GEOMETRY_REPAIR_EVENT = 'streamlain:canvas-geometry-repair';
const CORRUPT_XYWH_REPAIR_SIZE = 100;

export const promoteHydratedDefaultPropSymbol = Symbol(
  'streamlain:promote-hydrated-default-prop'
);

export type HydratedDefaultPropPromoter = {
  [promoteHydratedDefaultPropSymbol]?: (key: string) => void;
};

export const repairHydratedDefaultPropsAfterSyncSymbol = Symbol(
  'streamlain:repair-hydrated-default-props-after-sync'
);

export type HydratedDefaultPropsAfterSyncRepairer = {
  [repairHydratedDefaultPropsAfterSyncSymbol]?: () => number;
};

type DefaultPropRepairReason =
  | 'missing'
  | 'undefined'
  | 'invalid-xywh'
  | 'zero-size-xywh';

type PendingHydratedDefaultRepair = {
  defaultValue: unknown;
  key: string;
};

type CanvasGeometryRepairEventDetail = {
  readonly blockId: string;
  readonly docId?: string;
  readonly flavour: string;
  readonly key: string;
  readonly materialized: boolean;
  readonly persistedValue: unknown;
  readonly reason: DefaultPropRepairReason;
  readonly repairedValue: unknown;
  readonly yDocGuid?: string;
};

function getDefaultPropRepairReason(
  key: string,
  existingValue: unknown,
  defaultValue: unknown,
  hasExistingProp: boolean
): DefaultPropRepairReason | null {
  if (!hasExistingProp) {
    return 'missing';
  }
  if (existingValue === undefined) {
    return 'undefined';
  }
  if (key !== 'xywh') {
    return null;
  }

  const existingXYWH = parseXYWH(existingValue);
  if (!existingXYWH) {
    return 'invalid-xywh';
  }

  const defaultXYWH = parseXYWH(defaultValue);
  if (
    defaultXYWH &&
    defaultXYWH[2] > 0 &&
    defaultXYWH[3] > 0 &&
    (existingXYWH[2] <= 0 || existingXYWH[3] <= 0)
  ) {
    return 'zero-size-xywh';
  }

  return null;
}

function getHydratedDefaultPropValue(
  key: string,
  existingValue: unknown,
  defaultValue: unknown,
  repairReason: DefaultPropRepairReason
) {
  if (key !== 'xywh' || repairReason !== 'zero-size-xywh') {
    return defaultValue;
  }

  const existingXYWH = parseXYWH(existingValue);
  const defaultXYWH = parseXYWH(defaultValue);
  if (!existingXYWH || !defaultXYWH) {
    return defaultValue;
  }

  return serializeXYWH(
    existingXYWH[0],
    existingXYWH[1],
    existingXYWH[2] > 0 ? existingXYWH[2] : defaultXYWH[2],
    existingXYWH[3] > 0 ? existingXYWH[3] : defaultXYWH[3]
  );
}

function getExistingXYWHRepairReason(
  existingValue: unknown,
  hasExistingProp: boolean
): DefaultPropRepairReason | null {
  if (!hasExistingProp) {
    return null;
  }
  if (existingValue === undefined) {
    return 'undefined';
  }

  const existingXYWH = parseXYWH(existingValue);
  if (!existingXYWH) {
    return 'invalid-xywh';
  }
  if (existingXYWH[2] <= 0 || existingXYWH[3] <= 0) {
    return 'zero-size-xywh';
  }

  return null;
}

function getExistingXYWHRepairValue(existingValue: unknown) {
  const existingXYWH = parseXYWH(existingValue);
  return serializeXYWH(
    existingXYWH?.[0] ?? 0,
    existingXYWH?.[1] ?? 0,
    existingXYWH && existingXYWH[2] > 0
      ? existingXYWH[2]
      : CORRUPT_XYWH_REPAIR_SIZE,
    existingXYWH && existingXYWH[3] > 0
      ? existingXYWH[3]
      : CORRUPT_XYWH_REPAIR_SIZE
  );
}

function emitCanvasGeometryRepair(detail: CanvasGeometryRepairEventDetail) {
  if (detail.key !== 'xywh') {
    return;
  }

  const global = globalThis as typeof globalThis & {
    __STREAMLAIN_DEBUG_CANVAS_GEOMETRY__?: boolean;
  };

  try {
    if (
      typeof global.dispatchEvent === 'function' &&
      typeof CustomEvent === 'function'
    ) {
      global.dispatchEvent(
        new CustomEvent(CANVAS_GEOMETRY_REPAIR_EVENT, { detail })
      );
    }
  } catch {
    // Diagnostics must not affect document hydration.
  }

  const shouldWarn =
    global.__STREAMLAIN_DEBUG_CANVAS_GEOMETRY__ ||
    (typeof BUILD_CONFIG !== 'undefined' && BUILD_CONFIG.debug === true);
  if (shouldWarn) {
    console.warn('[streamlain] canvas geometry repaired', detail);
  }
}

function assertValidXYWHWrite(input: {
  blockId?: string;
  flavour?: string;
  key: string;
  value: unknown;
}) {
  if (input.key !== 'xywh') {
    return;
  }

  assertValidBlockXYWH(input.value, {
    blockId: input.blockId,
    flavour: input.flavour,
    key: input.key,
  });
}

function isInvalidHydratedDefaultValue(key: string, value: unknown) {
  if (key !== 'xywh') {
    return false;
  }

  const xywh = parseXYWH(value);
  return !xywh || xywh[2] <= 0 || xywh[3] <= 0;
}

/**
 * @internal
 * SyncController is responsible for syncing the block data with Yjs.
 * It creates a proxy model that syncs with Yjs and provides a reactive interface.
 * It also handles the stashing and popping of props.
 * It will also provide signals for block props.
 *
 */
export class SyncController {
  private _byPassProxy: boolean = false;

  private readonly _renderOnlyHydratedDefaultProps = new Set<string>();

  private readonly _pendingHydratedDefaultRepairs = new Map<
    string,
    PendingHydratedDefaultRepair
  >();

  private readonly _byPassUpdate = (fn: () => void) => {
    this._byPassProxy = true;
    fn();
    this._byPassProxy = false;
  };

  private readonly _mutex = createMutex();

  private _createPropSignal(model: BlockModel, key: string, value: unknown) {
    const data = signal(value);
    const dispose = effect(() => {
      const value = data.value;
      if (!this.model) return;
      this._mutex(() => {
        // @ts-expect-error allow magic props
        this.model.props[key] = value;
      });
    });
    const subscription = model.deleted.subscribe(() => {
      subscription.unsubscribe();
      dispose();
    });
    return data;
  }

  private readonly _observeYBlockChanges = () => {
    this.yBlock.observe(event => {
      event.keysChanged.forEach(key => {
        const type = event.changes.keys.get(key);
        if (!type) {
          return;
        }
        const isLocal = isLocalYjsChange(this.yBlock.doc, event.transaction);
        if (type.action === 'update' || type.action === 'add') {
          const value = this.yBlock.get(key);
          const keyName = key.replace('prop:', '');
          const proxy = this._getPropsProxy(keyName, value);
          this._renderOnlyHydratedDefaultProps.delete(keyName);
          if (!isInvalidHydratedDefaultValue(keyName, y2Native(value))) {
            this._pendingHydratedDefaultRepairs.delete(keyName);
          }
          this._byPassUpdate(() => {
            if (!this.model.keys.includes(keyName)) {
              this.model.keys.push(keyName);
            }
            // @ts-expect-error allow magic props
            this.model.props[keyName] = proxy;
            const signalKey = `${keyName}$`;
            if (!(signalKey in this.model.props)) {
              // @ts-expect-error allow magic props
              this.model.props[signalKey] = this._createPropSignal(
                this.model,
                keyName,
                y2Native(value)
              );
            }
            this._mutex(() => {
              if (signalKey in this.model.props) {
                // @ts-expect-error allow magic props
                this.model.props[signalKey].value = y2Native(value);
              }
            });
          });
          this.onChange?.(keyName, isLocal);
          return;
        }
        if (type.action === 'delete') {
          const keyName = key.replace('prop:', '');
          this._byPassUpdate(() => {
            // @ts-expect-error allow magic props
            delete this.model.props[keyName];
            if (`${keyName}$` in this.model.props) {
              // @ts-expect-error allow magic props
              this.model.props[`${keyName}$`].value = undefined;
            }
          });
          this.onChange?.(keyName, isLocal);
          return;
        }
      });
    });
  };

  private readonly _observeYChildrenChanges = () => {
    this.yChildren.observe(event => {
      this.onChange?.(
        'sys:children',
        isLocalYjsChange(this.yBlock.doc, event.transaction)
      );
    });
  };

  private readonly _stashed = new Set<string | number>();

  readonly flavour: string;

  readonly id: string;

  readonly model: BlockModel;

  readonly pop = (prop: string) => {
    if (!this._stashed.has(prop)) return;
    this._popProp(prop);
  };

  readonly stash = (prop: string) => {
    if (this._stashed.has(prop)) return;

    this._stashed.add(prop);
    this._stashProp(prop);
  };

  readonly version: number;

  readonly yChildren: Y.Array<string[]>;

  constructor(
    readonly schema: Schema,
    readonly yBlock: YBlock,
    readonly doc?: Store,
    readonly onChange?: (key: string, isLocal: boolean) => void
  ) {
    const { id, flavour, version, yChildren, props } = this._parseYBlock();

    this.id = id;
    this.flavour = flavour;
    this.yChildren = yChildren;
    this.version = version;

    this.model = this._createModel(props);

    this._observeYBlockChanges();
    this._observeYChildrenChanges();
  }

  private _createModel(props: UnRecord) {
    const _mutex = this._mutex;
    const schema = this.schema.flavourSchemaMap.get(this.flavour);
    if (!schema) {
      throw new BlockSuiteError(
        ErrorCode.ModelCRUDError,
        `schema for flavour: ${this.flavour} not found`
      );
    }

    const model = schema.model.toModel?.() ?? new BlockModel<object>();
    model.schema = schema;
    const signalWithProps = Object.entries(props).reduce(
      (acc, [key, value]) => {
        const data = this._createPropSignal(model, key, value);
        return {
          ...acc,
          [`${key}$`]: data,
          [key]: value,
        };
      },
      {} as Record<string, unknown>
    );

    model.id = this.id;
    model.keys = Object.keys(props);
    model.yBlock = this.yBlock;
    model.stash = this.stash;
    model.pop = this.pop;
    (model as BlockModel & HydratedDefaultPropPromoter)[
      promoteHydratedDefaultPropSymbol
    ] = this._promoteHydratedDefaultProp;
    (model as BlockModel & HydratedDefaultPropsAfterSyncRepairer)[
      repairHydratedDefaultPropsAfterSyncSymbol
    ] = this._repairHydratedDefaultPropsAfterSync;
    if (this.doc) {
      model.store = this.doc;
    }

    const proxy = new Proxy(signalWithProps, {
      has: (target, p) => {
        return Reflect.has(target, p);
      },
      set: (target, p, value, receiver) => {
        if (
          !this._byPassProxy &&
          typeof p === 'string' &&
          (model.keys.includes(p) || p === 'xywh')
        ) {
          const hadKey = model.keys.includes(p);
          if (this._stashed.has(p)) {
            assertValidXYWHWrite({
              blockId: model.id,
              flavour: model.flavour,
              key: p,
              value,
            });
            setValue(target, p, value);
            const result = Reflect.set(target, p, value, receiver);
            this.onChange?.(p, true);
            return result;
          }

          const yValue = native2Y(value);
          assertValidXYWHWrite({
            blockId: model.id,
            flavour: model.flavour,
            key: p,
            value,
          });

          if (this._renderOnlyHydratedDefaultProps.has(p)) {
            setValue(target, p, value);
            const result = Reflect.set(target, p, value, receiver);
            this.onChange?.(p, true);
            return result;
          }
          if (this.yBlock.get(`prop:${p}`) === yValue) {
            return Reflect.set(target, p, value, receiver);
          }
          this.yBlock.set(`prop:${p}`, yValue);
          if (!hadKey) {
            return true;
          }
          const proxy = this._getPropsProxy(p, yValue);
          setValue(target, p, value);
          return Reflect.set(target, p, proxy, receiver);
        }

        return Reflect.set(target, p, value, receiver);
      },
      get: (target, p, receiver) => {
        return Reflect.get(target, p, receiver);
      },
      deleteProperty: (target, p) => {
        if (
          !this._byPassProxy &&
          typeof p === 'string' &&
          model.keys.includes(p)
        ) {
          this.yBlock.delete(`prop:${p}`);
          setValue(target, p, undefined);
        }

        return Reflect.deleteProperty(target, p);
      },
    });
    model._props = proxy;

    function setValue(target: UnRecord, p: string, value: unknown) {
      _mutex(() => {
        // @ts-expect-error allow magic props
        target[`${p}$`].value = value;
      });
    }
    return model;
  }

  private _getPropsProxy(name: string, value: unknown) {
    return createYProxy(value, {
      onChange: (_, isLocal) => {
        this.onChange?.(name, isLocal);
        const signalKey = `${name}$`;
        if (signalKey in this.model.props) {
          this._mutex(() => {
            // @ts-expect-error allow magic props
            this.model.props[signalKey].value = y2Native(value);
          });
        }
      },
    });
  }

  private _parseYBlock() {
    let id: string | undefined;
    let flavour: string | undefined;
    let version: number | undefined;
    let yChildren: Y.Array<string[]> | undefined;
    const props: Record<string, unknown> = {};
    const rawProps: Record<string, unknown> = {};

    this.yBlock.forEach((value, key) => {
      if (key.startsWith('prop:')) {
        const keyName = key.replace('prop:', '');
        rawProps[keyName] = y2Native(value);
        props[keyName] = this._getPropsProxy(keyName, value);
        return;
      }
      if (key === 'sys:id' && typeof value === 'string') {
        id = value;
        return;
      }
      if (key === 'sys:flavour' && typeof value === 'string') {
        flavour = value;
        return;
      }
      if (key === 'sys:children' && value instanceof Y.Array) {
        yChildren = value;
        return;
      }
      if (key === 'sys:version' && typeof value === 'number') {
        version = value;
        return;
      }
    });

    if (!id) {
      throw new BlockSuiteError(
        ErrorCode.ModelCRUDError,
        'block id is not found when creating model'
      );
    }
    if (!flavour) {
      throw new BlockSuiteError(
        ErrorCode.ModelCRUDError,
        'block flavour is not found when creating model'
      );
    }
    if (!yChildren) {
      throw new BlockSuiteError(
        ErrorCode.ModelCRUDError,
        'block children is not found when creating model'
      );
    }

    const blockId = id;
    const schema = this.schema.flavourSchemaMap.get(flavour);
    if (!schema) {
      throw new BlockSuiteError(
        ErrorCode.ModelCRUDError,
        `schema for flavour: ${flavour} not found`
      );
    }
    const blockFlavour = flavour;
    const defaultProps = schema.model.props?.(internalPrimitives);

    if (typeof version !== 'number') {
      // no version found in data, set to schema version
      version = schema.version;
    }

    // Set default props if not exists
    if (defaultProps) {
      Object.entries(defaultProps).forEach(([key, value]) => {
        const hasExistingProp = key in props;
        const persistedValue = rawProps[key];
        const repairReason = getDefaultPropRepairReason(
          key,
          persistedValue,
          value,
          hasExistingProp
        );
        if (!repairReason) return;
        if (
          !shouldHydrateMissingDefaultProp({
            docId: this.doc?.id,
            flavour: blockFlavour,
            key,
            props,
          })
        ) {
          return;
        }

        const hydratedValue = getHydratedDefaultPropValue(
          key,
          persistedValue,
          value,
          repairReason
        );
        if (isInvalidHydratedDefaultValue(key, hydratedValue)) {
          return;
        }
        const yValue = native2Y(hydratedValue);
        const materialized =
          hydratedValue !== undefined &&
          shouldPersistHydratedDefaultProp({
            docId: this.doc?.id,
            flavour: blockFlavour,
            key,
            props,
          });
        if (materialized) {
          this.yBlock.set(`prop:${key}`, yValue);
        } else {
          this._renderOnlyHydratedDefaultProps.add(key);
          if (key === 'xywh') {
            this._pendingHydratedDefaultRepairs.set(key, {
              defaultValue: value,
              key,
            });
          }
        }
        props[key] = this._getPropsProxy(key, yValue);
        rawProps[key] = hydratedValue;
        emitCanvasGeometryRepair({
          blockId,
          docId: this.doc?.id,
          flavour: blockFlavour,
          key,
          materialized,
          persistedValue,
          reason: repairReason,
          repairedValue: hydratedValue,
          yDocGuid: this.yBlock.doc?.guid,
        });
      });
    }
    if (!defaultProps || !Object.hasOwn(defaultProps, 'xywh')) {
      const key = 'xywh';
      const persistedValue = rawProps[key];
      const repairReason = getExistingXYWHRepairReason(
        persistedValue,
        this.yBlock.has(`prop:${key}`)
      );
      if (repairReason) {
        const repairedValue = getExistingXYWHRepairValue(persistedValue);
        if (!isInvalidHydratedDefaultValue(key, repairedValue)) {
          const yValue = native2Y(repairedValue);
          this._renderOnlyHydratedDefaultProps.add(key);
          this._pendingHydratedDefaultRepairs.set(key, {
            defaultValue: repairedValue,
            key,
          });
          props[key] = this._getPropsProxy(key, yValue);
          rawProps[key] = repairedValue;
          emitCanvasGeometryRepair({
            blockId,
            docId: this.doc?.id,
            flavour: blockFlavour,
            key,
            materialized: false,
            persistedValue,
            reason: repairReason,
            repairedValue,
            yDocGuid: this.yBlock.doc?.guid,
          });
        }
      }
    }

    return {
      id,
      flavour,
      version,
      props,
      yChildren,
    };
  }

  private readonly _promoteHydratedDefaultProp = (prop: string) => {
    this._renderOnlyHydratedDefaultProps.delete(prop);
    this._pendingHydratedDefaultRepairs.delete(prop);
  };

  private readonly _repairHydratedDefaultPropsAfterSync = () => {
    let repairCount = 0;

    for (const [key, repair] of this._pendingHydratedDefaultRepairs) {
      const yKey = `prop:${key}`;
      const hasPersistedProp = this.yBlock.has(yKey);
      const persistedValue = y2Native(this.yBlock.get(yKey));
      const repairReason = getDefaultPropRepairReason(
        key,
        persistedValue,
        repair.defaultValue,
        hasPersistedProp
      );

      if (!repairReason) {
        this._pendingHydratedDefaultRepairs.delete(key);
        continue;
      }

      const repairedValue = getHydratedDefaultPropValue(
        key,
        persistedValue,
        repair.defaultValue,
        repairReason
      );
      if (isInvalidHydratedDefaultValue(key, repairedValue)) {
        continue;
      }

      assertValidXYWHWrite({
        blockId: this.id,
        flavour: this.flavour,
        key,
        value: repairedValue,
      });

      this.yBlock.set(yKey, native2Y(repairedValue));
      this._renderOnlyHydratedDefaultProps.delete(key);
      this._pendingHydratedDefaultRepairs.delete(key);
      repairCount++;
    }

    return repairCount;
  };

  private _popProp(prop: string) {
    const model = this.model as BlockModel<Record<string, unknown>>;

    const value = model.props[prop];
    this._stashed.delete(prop);
    this._promoteHydratedDefaultProp(prop);
    model.props[prop] = value;
  }

  private _stashProp(prop: string) {
    if (this._renderOnlyHydratedDefaultProps.has(prop)) {
      return;
    }

    (this.model as BlockModel<Record<string, unknown>>).props[prop] = y2Native(
      this.yBlock.get(`prop:${prop}`),
      {
        transform: (value, origin) => {
          if (Boxed.is(origin)) {
            return value;
          }
          if (origin instanceof Y.Map) {
            return new Proxy(value as UnRecord, {
              get: (target, p, receiver) => {
                return Reflect.get(target, p, receiver);
              },
              set: (target, p, value, receiver) => {
                const result = Reflect.set(target, p, value, receiver);
                this.onChange?.(prop, true);
                return result;
              },
              deleteProperty: (target, p) => {
                const result = Reflect.deleteProperty(target, p);
                this.onChange?.(prop, true);
                return result;
              },
            });
          }
          if (origin instanceof Y.Array) {
            return new Proxy(value as unknown[], {
              get: (target, p, receiver) => {
                return Reflect.get(target, p, receiver);
              },
              set: (target, p, value, receiver) => {
                const index = Number(p);
                if (Number.isNaN(index)) {
                  return Reflect.set(target, p, value, receiver);
                }
                const result = Reflect.set(target, p, value, receiver);
                this.onChange?.(prop, true);
                return result;
              },
              deleteProperty: (target, p) => {
                const result = Reflect.deleteProperty(target, p);
                this.onChange?.(p as string, true);
                return result;
              },
            });
          }

          return value;
        },
      }
    );
  }
}
